import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Flatpickr from 'react-flatpickr';
import "flatpickr/dist/flatpickr.min.css";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('all');
    const [dateRange, setDateRange] = useState([]); // Flatpickr// UI selection
    const [appliedDateRange, setAppliedDateRange] = useState([]);


    useEffect(() => {
        fetchOrders(1);
    }, [paymentStatus, search, appliedDateRange]);


    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3;

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link text-gray-900"
                            onClick={() => fetchOrders(current - 1)}
                        >
                            Previous
                        </button>
                    </li>

                    {start > 1 && (
                        <>
                            <li className="page-item">
                                <button
                                    className="page-link text-gray-900"
                                    onClick={() => fetchOrders(1)}
                                >
                                    1
                                </button>
                            </li>
                            {start > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}

                    {pageNumbers.map(num => (
                        <li
                            key={num}
                            className={`page-item ${num === current ? 'active' : ''}`}
                        >
                            <button
                                className={`page-link text-gray-900 ${
                                    num === current
                                        ? '!bg-purple-500 !text-white !border-purple-500 border'
                                        : ''
                                }`}
                                onClick={() => fetchOrders(num)}
                            >
                                {num}
                            </button>
                        </li>
                    ))}

                    {end < last && (
                        <>
                            {end < last - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link text-gray-900"
                                    onClick={() => fetchOrders(last)}
                                >
                                    {last}
                                </button>
                            </li>
                        </>
                    )}

                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button
                            className="page-link text-gray-900"
                            onClick={() => fetchOrders(current + 1)}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };


    const fetchOrders = async (pageNumber = 1) => {
        try {
            setLoading(true);

            const params = {
                page: pageNumber,
                payment_status: paymentStatus !== 'all' ? paymentStatus : undefined,
                search: search || undefined,
                date_from: appliedDateRange[0]
                    ? appliedDateRange[0].toLocaleDateString('en-CA')
                    : undefined,

                date_to: appliedDateRange[1]
                    ? appliedDateRange[1].toLocaleDateString('en-CA')
                    : undefined,
            };

            const response = await api.get('/organizer/orders', { params });

            const paginator = response.data.data.orders;
            setOrders(paginator.data || []);
            setPagination(paginator);
            setStats(response.data.data.stats || {});
            setPage(pageNumber);
        } catch {
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const handleResetFilters = () => {
        setSearch('');
        setPaymentStatus('all');
        setDateRange([]);
        setAppliedDateRange([]);
        // Note: This will trigger useEffect to fetch orders with reset filters
    };

    const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `${parseFloat(amount).toFixed(2)}`;
  };

  const getItemLabel = (item) => {
    if (item.ticket_category?.category_name) return item.ticket_category.category_name;
    if (item.section?.name) return `${item.section.name} (GA)`;
    return 'Item';
  };

  const getTicketLabel = (ticket) => {
    if (ticket.ticket_category?.category_name) return ticket.ticket_category.category_name;
    if (ticket.seat_map_seat_id) {
      return [ticket.section?.name, ticket.seat?.label].filter(Boolean).join(' · ') || 'Reserved Seat';
    }
    if (ticket.seat_map_section_id) return (ticket.section?.name || 'General Admission') + ' (GA)';
    return 'Ticket';
  };

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(search) ||
      order.user?.first_name?.toLowerCase().includes(search) ||
      order.user?.last_name?.toLowerCase().includes(search) ||
      order.event?.title?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      confirmed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return badges[status] || 'badge-secondary';
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      completed: 'badge-success',
      paid: 'badge-success',
      failed: 'badge-danger',
      refunded: 'badge-info'
    };
    return badges[status] || 'badge-secondary';
  };

  const handleViewDetails = async (order) => {
    setSelectedOrder(order);
    setShowDrawer(true);
    setLoadingDetails(true);
    
    try {
      const response = await api.get(`/organizer/orders/${order.id}`);
      setOrderDetails(response.data.data);
    } catch (error) {
      toast.error('Failed to load order details');
      console.error('Error fetching order details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setSelectedOrder(null);
    setOrderDetails(null);
  };

    const generateCSV = (csvData, filename = `orders_${new Date().toISOString().split('T')[0]}.csv`) => {
        if (!csvData || !csvData.length) return;

        const csvContent = [
            Object.keys(csvData[0]).join(','), // headers
            ...csvData.map(row =>
                Object.values(row)
                    .map(val => `"${val}"`) // wrap values in quotes
                    .join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleExportCSV = async () => {
        try {
            const response = await api.get('/organizer/orders/export', {
                params: {
                    payment_status: paymentStatus !== 'all' ? paymentStatus : undefined,
                    search: search || undefined,
                    date_from: appliedDateRange[0]?.toLocaleDateString('en-CA'),
                    date_to: appliedDateRange[1]?.toLocaleDateString('en-CA'),
                }
            });

            const allOrders = response.data; // filtered orders

            const csvData = allOrders.map(order => {
                // flatten items into a string
                const itemsStr = order.items?.map(item =>
                    `${item.quantity}x ${getItemLabel(item)} (${item.price})`
                ).join('; ') || '';

                return {
                    'Order #': order.id,
                    'Date': formatDate(order.created_at),
                    'Event': order.event?.title,
                    'Customer': `${order.user?.first_name} ${order.user?.last_name}`,
                    'Email': order.user?.email,
                    'Items': itemsStr, // added items column
                    'Subtotal': order.total_amount,
                    'Discount': order.discount_total,
                    'Total': order.total_amount - order.discount_total,
                    'Currency': order.event?.currency,
                    'Payment Status': order.payment_status,
                };
            });

            generateCSV(csvData);
            toast.success('Orders exported successfully');
        } catch {
            toast.error('Export failed');
        }
    };


    return (
    <div>
      {/* Page Heading */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h5 className="h5 mb-0 font-weight-bold text-gray-900">
          Orders Management
        </h5>
        <button 
          className="btn btn-success btn-sm"
          onClick={handleExportCSV}
          disabled={filteredOrders.length === 0}
        >
          <i className="fas fa-download mr-2"></i>
          Export CSV
        </button>
      </div>

      {/* Stats Row */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Total Orders</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.total_orders || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-shopping-cart fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Total Revenue</div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {stats.total_revenue && Object.keys(stats.total_revenue).length > 0 ? (
                            Object.entries(stats.total_revenue).map(([currency, amount]) => (
                                <div key={currency}>
                                    {currency}: {formatCurrency(amount)}
                                </div>
                            ))
                        ) : (
                            formatCurrency(0)
                        )}
                    </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Pending Orders</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.pending_orders || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-clock fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Completed Orders</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.completed_orders || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        <div className="bg-white rounded-lg shadow p-6 mb-4 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search
                    </label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by user or event..."
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
                    />
                </div>

                {/* Status Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Status
                    </label>
                    <select
                        value={paymentStatus}
                        onChange={(e) => setPaymentStatus(e.target.value)}
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
                    >
                        <option value="all">All Payments</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>

                {/* Date Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                    </label>
                    <Flatpickr
                        value={dateRange}
                        options={{
                            mode: 'range',
                            dateFormat: 'Y-m-d',
                            showMonths: 1,
                            // Disable auto-apply on change
                            onValueUpdate: (selectedDates, dateStr, instance) => {
                                // Don't update state here, let onChange handle it
                            },
                        }}
                        onChange={(dates) => {
                            // Only update when two dates are selected
                            if (dates.length === 2) {
                                setDateRange(dates);
                                setAppliedDateRange(dates); // Apply immediately when both dates selected
                            }
                            // If user clears, reset
                            if (dates.length === 0) {
                                setDateRange([]);
                                setAppliedDateRange([]);
                            }
                        }}
                        placeholder="Select date range"
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
                    />
                </div>

                <div className="flex items-end">
                    <button
                        onClick={handleResetFilters}
                        className="btn btn-outline-danger w-20"
                        disabled={!search && paymentStatus === 'all' && dateRange.length === 0}
                    >
                        <i className="fas fa-redo mr-2"></i>
                        Reset
                    </button>
                </div>

            </div>
        </div>

      {/* Orders Table Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="m-0 font-weight-bold text-gray-900">All Orders</h6>
            </div>
          </div>
        </div>
        <div className="card-body p-4">
            {loading ? (
                    <div className="text-center py-12">
                        <svg
                            className="animate-spin h-12 w-12 mx-auto text-purple-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <p className="mt-4 text-gray-600">Loading orders...</p>
                    </div>
                ) :
                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table className="table table-hover mb-0">
              <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ fontSize: '0.8rem' }}>Order #</th>
                  <th style={{ fontSize: '0.8rem' }}>Date</th>
                  <th style={{ fontSize: '0.8rem' }}>Event</th>
                  <th style={{ fontSize: '0.8rem' }}>Customer</th>
                  <th style={{ fontSize: '0.8rem' }}>Items</th>
                  <th style={{ fontSize: '0.8rem' }}>Total</th>
                  <th style={{ fontSize: '0.8rem' }}>Payment</th>
                  <th style={{ fontSize: '0.8rem' }}>Status</th>
                  <th style={{ fontSize: '0.8rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr key={order.id} style={{ fontSize: '0.85rem' }}>
                      <td className="font-weight-bold text-purple-500">
                        #{order.id}
                      </td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '200px' }}>
                          {order.event?.title}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="font-weight-bold">{order.user?.first_name} {order.user?.last_name}</div>
                          <small className="text-muted">{order.user?.email}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          {order.items?.length > 0 ? (
                            order.items.map((item, idx) => (
                              <div key={idx} className="small">
                                {item.quantity}x {getItemLabel(item)}
                              </div>
                            ))
                          ) : (
                            (order.tickets || []).filter(t => t.seat_map_seat_id).map((ticket, idx) => (
                              <div key={idx} className="small">
                                1x {[ticket.section?.name, ticket.seat?.label].filter(Boolean).join(' · ') || 'Reserved Seat'}
                              </div>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="font-weight-bold">{formatCurrency(order.total_amount - order.discount_total)} {order.event?.currency || ''}</td>
                      <td>
                        <span className={`badge ${getPaymentStatusBadge(order.payment_status)}`}>
                          {order.payment_status}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadge(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-xs btn-outline-success"
                          onClick={() => handleViewDetails(order)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      {searchTerm ? 'No orders found matching your search' : 'No orders yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
            }
        </div>
          {renderPagination()}
      </div>

      {/* Order Details Drawer */}
      {showDrawer && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={{ zIndex: 1040 }}
            onClick={handleCloseDrawer}
          ></div>
          <div 
            className="bg-white shadow-lg position-fixed" 
            style={{ 
              top: 0, 
              right: 0, 
              width: '600px', 
              height: '100vh', 
              zIndex: 1050,
              overflowY: 'auto',
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            <div className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                  <i className="fas fa-shopping-cart mr-2 text-purple-500"></i>
                  Order #{selectedOrder?.id}
                </h4>
                <button 
                  type="button" 
                  className="btn btn-link text-dark" 
                  onClick={handleCloseDrawer}
                >
                  <i className="fas fa-times fa-lg"></i>
                </button>
              </div>

              {loadingDetails ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-purple-500" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : orderDetails && (
                <>
                  {/* Order Status */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-6 mb-3">
                          <small className="text-muted">Payment Status</small>
                          <div>
                            <span className={`badge ${getPaymentStatusBadge(orderDetails.payment_status)}`}>
                              {orderDetails.payment_status}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-muted">Order Date</small>
                          <div>{formatDate(orderDetails.created_at)}</div>
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-muted">Payment Method</small>
                          <div className="text-capitalize">{orderDetails.payment_gateway || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="text-purple-500 font-weight-bold mb-3">
                        <i className="fas fa-user mr-2"></i>Customer Information
                      </h6>
                      <div>
                        <div className="font-weight-bold">
                          {orderDetails.user?.first_name} {orderDetails.user?.last_name}
                        </div>
                        <div className="text-muted small">{orderDetails.user?.email}</div>
                        <div className="text-muted small">{orderDetails.user?.mobile}</div>
                      </div>
                    </div>
                  </div>

                  {/* Event Info */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="text-purple-500 font-weight-bold mb-3">
                        <i className="fas fa-calendar-alt mr-2"></i>Event Information
                      </h6>
                      <div className="font-weight-bold">{orderDetails.event?.title}</div>
                      <div className="text-muted small">
                        <i className="fas fa-map-marker-alt mr-1"></i>
                        {orderDetails.event?.location}
                      </div>
                      <div className="text-muted small">
                        <i className="fas fa-clock mr-1"></i>
                        {formatDate(orderDetails.event?.event_start)}
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="text-purple-500 font-weight-bold mb-3">
                        <i className="fas fa-list mr-2"></i>Order Items
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th>Category</th>
                              <th>Qty</th>
                              <th>Price</th>
                              <th>Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderDetails.items?.length > 0 ? (
                              orderDetails.items.map((item, idx) => (
                                <tr key={idx}>
                                  <td>{getItemLabel(item)}</td>
                                  <td>{item.quantity}</td>
                                  <td>{formatCurrency(item.price)}</td>
                                  <td>{formatCurrency(item.price * item.quantity)}</td>
                                </tr>
                              ))
                            ) : (
                              (orderDetails.tickets || []).filter(t => t.seat_map_seat_id).length > 0 ? (
                                (orderDetails.tickets || []).filter(t => t.seat_map_seat_id).map((ticket, idx) => (
                                  <tr key={idx}>
                                    <td>{[ticket.section?.name, ticket.seat?.label].filter(Boolean).join(' · ') || 'Reserved Seat'}</td>
                                    <td>1</td>
                                    <td>{formatCurrency(ticket.amount)}</td>
                                    <td>{formatCurrency(ticket.amount)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center text-muted small">No items</td>
                                </tr>
                              )
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th colSpan="3" className="text-right">Subtotal:</th>
                              <th>{formatCurrency(orderDetails.total_amount)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-right">Discount:</th>
                                <th>{formatCurrency(orderDetails.discount_total)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-right">Total:</th>
                                <th>{formatCurrency(orderDetails.total_amount - orderDetails.discount_total )}</th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Tickets */}
                  {orderDetails.tickets && orderDetails.tickets.length > 0 && (
                    <div className="card mb-3">
                      <div className="card-body">
                        <h6 className="text-purple-500 font-weight-bold mb-3">
                          <i className="fas fa-ticket-alt mr-2"></i>
                          Tickets ({orderDetails.tickets.length})
                        </h6>
                        <div className="list-group">
                          {orderDetails.tickets.map(ticket => (
                            <div key={ticket.id} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                  <div className="font-weight-bold">
                                    <i className="fas fa-qrcode mr-2 text-muted"></i>
                                    #{ticket.id}
                                  </div>
                                  <small className="text-muted">
                                    {getTicketLabel(ticket)}
                                  </small>
                                </div>
                                <div className="text-right">
                                  <span className={`badge badge-${
                                    ticket.status === 'active' ? 'success' :
                                    ticket.status === 'used' ? 'secondary' :
                                    ticket.status === 'cancelled' ? 'danger' : 'warning'
                                  }`}>
                                    {ticket.status}
                                  </span>
                                  {ticket.scanned_at && (
                                    <div className="small text-success mt-1">
                                      <i className="fas fa-check-circle mr-1"></i>
                                      Scanned: {formatDate(ticket.scanned_at)}
                                    </div>
                                  )}
                                  <small className="text-muted d-block">
                                    {formatCurrency(ticket.amount)} {orderDetails.event?.currency ?? ''}
                                  </small>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Orders;
